// ./src/pages/private/EventQuestionsPage/QuestionDrawer.tsx
//
// Add / edit one registration question (asked when a guest responds or buys a
// ticket). Fields: the prompt, the answer type (typed / pick-one / pick-several /
// file), an optional help text + placeholder, and required toggle. Choice types
// carry a list of options (label + optional extra cost) that are synced to the
// rsvp_question_option children on save. Uses the shared AppDrawer + form fields.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Box, Text, ActionIcon, Divider, Input } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import {
  RsvpQuestion,
  RsvpQuestionInput,
  RsvpQuestionOption,
  RsvpQuestionService,
  RsvpQuestionOptionService,
  RsvpQuestionConditionService,
  GuestGroupService,
  GuestGroupSetService,
  GuestGroup,
  GuestGroupSet,
  QUESTION_ASKED_ON,
  PARTNER_KIND_VALUES,
  partnerKindLabel,
} from '@/utils/api';
import { useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { getFormErrors, formatErrors } from '@/utils';
import {
  AppDrawer,
  AppIcon,
  TextField,
  TextAreaField,
  SelectField,
  NumberField,
  SwitchField,
  MultiSelectField,
} from '@/components/generic';
import { RsvpStatusMultiSelect } from '@/components/features/Events';
import type { QuestionPrefill } from './questionPrefill';
import { adoptSavedOptionIds, OptionRow, refusalsOf } from './questionOptions';

const SINGLE_VALUE = 'single_value';
const SINGLE_CHOICE = 'single_choice';
const MULTI_CHOICE = 'multi_choice';
const FILE = 'file';
const ADDRESS = 'address';
const IMAGE = 'image';

const QUESTION_TYPES = [
  { value: SINGLE_VALUE, label: 'Typed answer' },
  { value: SINGLE_CHOICE, label: 'Pick one' },
  { value: MULTI_CHOICE, label: 'Pick several' },
  { value: FILE, label: 'File upload' },
  // A2e #86 — one question asked as a whole postal address (street with autocomplete, city, postal code, country…).
  { value: ADDRESS, label: 'Address' },
  // A2e #91 — one picture, cropped and checked on upload: a guest's photo, a partner's logo.
  { value: IMAGE, label: 'Picture (photo or logo)' },
];

/** A2e #91 — what a picture question asks for. The site crops to it and the server refuses anything else. */
const IMAGE_SHAPES = [
  { value: 'avatar', label: 'Photo — square, at least 512 × 512 pixels' },
  { value: 'logo', label: 'Logo — square or wider (up to 2:1), at least 200 × 100 pixels' },
];

/**
 * A2e #91 — where a picture may also go, per form (the server refuses any other pairing): on the RSVP form the guest's
 * photo on this event's site (it must be a square photo), on the sponsorship form the partner's logo (set when the
 * application is approved). A volunteer's picture goes nowhere else.
 */
const IMAGE_LINKS: Record<string, { value: string; label: string }[]> = {
  [QUESTION_ASKED_ON.RSVP]: [{ value: 'photo', label: 'The guest’s photo on this event’s website' }],
  [QUESTION_ASKED_ON.PARTNER_APPLICATION]: [{ value: 'logo', label: 'The partner’s logo (when the application is approved)' }],
};

/** The shape a stored question asks for — `avatar` unless its settings say `logo`. */
const imageShapeOf = (settings: unknown): string => {
  const image = (settings as { image?: { shape?: unknown } } | null | undefined)?.image;

  return image?.shape === 'logo' ? 'logo' : 'avatar';
};

// The value type drives validation + which input widget is shown to the guest.
const VALUE_TYPES = [
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text (paragraph)' },
  { value: 'integer', label: 'Whole number' },
  { value: 'decimal', label: 'Number' },
  { value: 'amount', label: 'Currency' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'Website' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Checkbox / switch' },
];

// Only text-like inputs get a placeholder.
const TEXT_VALUE_TYPES = new Set(['short_text', 'long_text', 'phone', 'email', 'url']);

// Save the answer onto the guest's profile. A person field can only be chosen
// when the answer's value type matches it (phone → phone, birthday → date, …);
// some fields accept more than one format.
const PERSON_FIELDS: { value: string; label: string; vts: string[] }[] = [
  { value: 'nameTitle', label: 'Title (Mr/Ms/Dr)', vts: ['short_text'] },
  { value: 'middleName', label: 'Middle name', vts: ['short_text'] },
  { value: 'nameSuffix', label: 'Suffix (Jr/Sr)', vts: ['short_text'] },
  { value: 'email', label: 'Email', vts: ['email'] },
  { value: 'phone', label: 'Phone', vts: ['phone'] },
  { value: 'birthday', label: 'Birthday', vts: ['date'] },
  { value: 'sex', label: 'Sex', vts: ['short_text'] },
  { value: 'gender', label: 'Gender', vts: ['short_text'] },
  { value: 'pronouns', label: 'Pronouns', vts: ['short_text'] },
  { value: 'personalWebsite', label: 'Website', vts: ['url'] },
  { value: 'socialUrl1', label: 'Social media link', vts: ['url'] },
  { value: 'dietaryRestrictions', label: 'Dietary restrictions', vts: ['short_text', 'long_text'] },
  { value: 'company', label: 'Company', vts: ['short_text'] },
  { value: 'jobTitle', label: 'Job title', vts: ['short_text'] },
  { value: 'city', label: 'City', vts: ['short_text'] },
  { value: 'stateProvince', label: 'State / province', vts: ['short_text'] },
  { value: 'country', label: 'Country', vts: ['short_text'] },
  { value: 'postalCode', label: 'Postal code', vts: ['short_text'] },
  { value: 'streetAddress', label: 'Street address', vts: ['short_text'] },
];

/**
 * A2e #86 — the ONLY link an address question can carry: the whole address, written to all six of the contact's
 * address fields. The server refuses any other pairing (an address to one part, or `address` on a typed answer).
 */
const ADDRESS_PERSON_FIELD = { value: 'address', label: 'Address (street, city, postal code, country…)' };

const IMAGE_LINK_VALUES = new Set(['photo', 'logo']);

const RSVP_STATUS_CONDITION = 'rsvp_status';
const GUEST_GROUP_CONDITION = 'guest_group';



// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
const KNOWN_FORM_FIELDS = ['title', 'questionType', 'valueType', 'personField', 'isRequired', 'requiredScope', 'askOncePerGroup', 'helpText', 'placeholder', 'options', 'rsvpStatuses'];

/**
 * WHO has to answer. Replaces a plain Required switch, because "required of everyone" is wrong for
 * a contact question on a party with a child in it: the person filling the form has no address for
 * the child, so they enter their own — and people are matched by email, so the child then resolves
 * to the adult's contact and inherits their guest row.
 */
const REQUIRED_SCOPES = [
  { value: 'none', label: 'Optional' },
  { value: 'all', label: 'Everyone' },
  { value: 'primary', label: 'Main guest only' },
  { value: 'adults', label: 'Adults only' },
];

interface QuestionForm {
  title: string;
  questionType: string;
  valueType: string;
  personField: string | null;
  requiredScope: string;
  askOncePerGroup: boolean;
  helpText: string;
  placeholder: string;
  options: OptionRow[];
  rsvpStatuses: string[];
  guestGroupIds: string[];
  /** Partner form only: the kinds this question is asked of. Empty = every kind. */
  partnerKinds: string[];
  /** A2e #91 — a picture question's shape (`settings.image.shape`): avatar | logo. */
  imageShape: string;
}

const isChoiceType = (qt: string): boolean => qt === SINGLE_CHOICE || qt === MULTI_CHOICE;
const EMPTY_OPTION: OptionRow = { label: '', price: null };

// Keep exactly one trailing empty row for choice types (the "add" affordance).
const withTrailingEmpty = (options: OptionRow[]): OptionRow[] => {
  const last = options[options.length - 1];
  return last && last.label.trim() !== '' ? [...options, { ...EMPTY_OPTION }] : options.length === 0 ? [{ ...EMPTY_OPTION }] : options;
};

const emptyForm = (): QuestionForm => ({
  title: '',
  questionType: SINGLE_VALUE,
  valueType: 'short_text',
  personField: null,
  requiredScope: 'none',
  askOncePerGroup: false,
  helpText: '',
  placeholder: '',
  options: [],
  rsvpStatuses: [],
  guestGroupIds: [],
  partnerKinds: [],
  imageShape: 'avatar',
});

/**
 * A blank form seeded from a prefill.
 *
 * ⚠ `valueType` is DERIVED from the person field, never taken from the URL: a person field only
 * accepts certain value types (birthday ⇒ date), and the drawer's own `valueType` handler DROPS a
 * person field that doesn't match the type. A mismatched pair would silently produce a question
 * with no person link — the very state we are here to fix. An unrecognised field seeds nothing.
 */
const seededForm = (prefill?: QuestionPrefill | null): QuestionForm => {
  const base = emptyForm();
  if (!prefill) {
    return base;
  }
  const field = PERSON_FIELDS.find((p) => p.value === prefill.personField);
  // A2e #86 — "ask for their address" is an address question linked to the whole address, never a typed one.
  const wantsAddress = prefill.personField === ADDRESS_PERSON_FIELD.value;

  const questionType = wantsAddress
    ? ADDRESS
    : QUESTION_TYPES.some((q) => q.value === prefill.questionType)
      ? (prefill.questionType as string)
      : base.questionType;
  const choiceOptions = (prefill.options ?? []).filter((label) => label.trim()).map((label) => ({ label, price: null }));

  return {
    ...base,
    title: (prefill.title ?? '').trim() || field?.label || (wantsAddress ? 'Address' : base.title),
    questionType,
    // The person field decides the value type (see above); otherwise a known one from the prefill.
    valueType: field ? field.vts[0] : VALUE_TYPES.some((v) => v.value === prefill.valueType) ? (prefill.valueType as string) : base.valueType,
    personField: wantsAddress ? ADDRESS_PERSON_FIELD.value : field ? field.value : base.personField,
    requiredScope: REQUIRED_SCOPES.some((r) => r.value === prefill.requiredScope)
      ? (prefill.requiredScope as string)
      : prefill.isRequired
        ? 'all'
        : base.requiredScope,
    askOncePerGroup: !!prefill.askOncePerGroup,
    helpText: prefill.helpText ?? base.helpText,
    options: isChoiceType(questionType) ? withTrailingEmpty(choiceOptions) : base.options,
  };
};

const toForm = (q: RsvpQuestion): QuestionForm => {
  const questionType = q.questionType ?? SINGLE_VALUE;
  const options = (q.options ?? [])
    .slice()
    .sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0))
    .map((o) => ({ id: o.id, label: o.label ?? '', price: o.price ?? null }));
  const statusCond = (q.conditions ?? []).find((c) => c.conditionKind === RSVP_STATUS_CONDITION);
  const guestGroupIds = (q.conditions ?? [])
    .filter((c) => c.conditionKind === GUEST_GROUP_CONDITION && c.guestGroupId)
    .map((c) => String(c.guestGroupId));
  return {
    title: q.title ?? '',
    questionType,
    valueType: q.valueType ?? 'short_text',
    personField: q.personField ?? null,
    // Older rows have no scope; the boolean says what it always meant.
    requiredScope: q.requiredScope ?? (q.isRequired ? 'all' : 'none'),
    askOncePerGroup: !!q.askOncePerGroup,
    partnerKinds: Array.isArray(q.partnerKinds) ? (q.partnerKinds as string[]) : [],
    helpText: q.helpText ?? '',
    placeholder: q.placeholder ?? '',
    options: isChoiceType(questionType) ? withTrailingEmpty(options) : options,
    rsvpStatuses: statusCond?.rsvpStatuses ?? [],
    guestGroupIds,
    imageShape: imageShapeOf(q.settings),
  };
};

export interface QuestionDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  question: RsvpQuestion | null;
  /** Seeds a NEW question (ignored when editing). See `QuestionPrefill`. */
  prefill?: QuestionPrefill | null;
  /**
   * ⚠ WHICH FORM a new question is created on — required, no default. The server refuses a create
   * without it and `asked_on` is NOT NULL with no database default, so there is nothing to fall
   * back to. Create-only: an existing question never moves between forms, because its answers hang
   * off an event_guest on one and a partner_application on the other.
   */
  askedOn: string;
  /** The saved question's id — the page highlights its row (the confirmation; there is no success toast). */
  onSaved: (questionId: string | null) => void;
  /**
   * The event counts a PARTY per reply (headcount — owner, 2026-10-22): only the main guest is named, so they answer
   * every question ONCE for the whole party. "Everyone", "Adults only" and "once per group" mean nothing there, and
   * offering them read as if each guest would be asked. RSVP form only.
   */
  headcountParty?: boolean;
}

/** On a headcount event: answered or not — the main guest answers for the party. */
const PARTY_SCOPES = [
  { value: 'none', label: 'Optional' },
  { value: 'all', label: 'Required' },
];

export const QuestionDrawer: React.FC<QuestionDrawerProps> = ({ opened, onClose, eventId, question, prefill, askedOn, onSaved, headcountParty = false }) => {
  // ⚠ Which form this drawer is editing. Several controls are meaningless on the partner form and
  // are hidden rather than shown doing nothing — see each one.
  const isPartnerForm = askedOn === QUESTION_ASKED_ON.PARTNER_APPLICATION;
  // An APPLICATION form (sponsorship or volunteer, #A4.6) has ONE answerer and no RSVP status / guest groups, so
  // the RSVP-only switches below mean nothing there.
  const isApplicationForm = askedOn !== QUESTION_ASKED_ON.RSVP;
  // A headcount event's RSVP form: one answer per party (the main guest's). An older question keeps a stored scope the
  // short list does not name, so it is shown rather than read as blank.
  const partyOnly = headcountParty && !isApplicationForm;
  const { t } = useTranslation(['events', 'common']);
  const [form, setForm] = useState<QuestionForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  // The question as the server holds it — what Save updates and what its options / conditions are diffed against. The
  // `question` prop, or the one this drawer CREATED: once created, Save again must update it, never create a second (#126).
  const [base, setBase] = useState<RsvpQuestion | null>(null);
  // The question saved but some of its options / conditions did not — said above Save, the drawer stays open (#126).
  const [partErrors, setPartErrors] = useState<OpResult[]>([]);
  // After a half-failed save whose re-read ALSO failed, `base` is not known to match the server (its options may predate
  // the ones that saved): the next Save re-reads it before writing anything, or a retry would create them twice.
  const [baseUnconfirmed, setBaseUnconfirmed] = useState(false);
  // The event's guest groups (across sets), for the group-visibility condition picker.
  const [groupOptions, setGroupOptions] = useState<{ group: string; items: { value: string; label: string }[] }[]>([]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const titleRef = useRef<HTMLInputElement>(null);

  // Esc / a click outside / Cancel ask before discarding a changed question (#123). A prefill is part of the
  // snapshot: the host did not type it, so an untouched prefilled question closes without asking.
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();
      setBase(null);
      setPartErrors([]);
      setBaseUnconfirmed(false);

      return;
    }

    // A refreshed question keeps what the user has typed (#125); another question replaces the form.
    const seeded = question ? toForm(question) : seededForm(prefill);
    setForm(reseed(seeded, question?.id ?? '__new__'));
    setBase(question ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, question]);

  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => {
      const el = titleRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 120);
    return () => clearTimeout(id);
  }, [opened]);

  // Load the event's guest groups (across sets) once, for the group-visibility picker.
  useEffect(() => {
    if (!opened || !eventId) return undefined;
    let alive = true;
    (async () => {
      const setsRes = await GuestGroupSetService.list(eventId);
      if (!alive || !setsRes.didSucceed()) return;
      const sets = (setsRes.data ?? []) as GuestGroupSet[];
      const perSet = await Promise.all(sets.map((s) => GuestGroupService.list(String(s.id))));
      if (!alive) return;
      const opts = sets
        .map((s, i) => {
          const res = perSet[i];
          const groups = (res?.didSucceed() ? res.data ?? [] : []) as GuestGroup[];
          return {
            group: s.name ?? 'Group set',
            items: groups.filter((g) => g.id).map((g) => ({ value: String(g.id), label: g.name ?? 'Group' })),
          };
        })
        .filter((o) => o.items.length > 0);
      setGroupOptions(opts);
    })();
    return () => {
      alive = false;
    };
  }, [opened, eventId]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => {
      // Switching to a choice type seeds an empty option row; a non-typed
      // question can't map to a person field.
      if (name === 'questionType') {
        const qt = value as string;
        return {
          ...f,
          questionType: qt,
          options: isChoiceType(qt) ? withTrailingEmpty(f.options) : f.options,
          // A typed answer keeps a typed link, an address keeps the whole-address one, a picture keeps a picture link;
          // anything else has none.
          personField:
            qt === SINGLE_VALUE && f.personField !== ADDRESS_PERSON_FIELD.value && !IMAGE_LINK_VALUES.has(String(f.personField))
              ? f.personField
              : qt === ADDRESS && f.personField === ADDRESS_PERSON_FIELD.value
                ? f.personField
                : qt === IMAGE && IMAGE_LINK_VALUES.has(String(f.personField))
                  ? f.personField
                  : null,
        };
      }
      // Changing the value type may invalidate the mapped person field.
      if (name === 'valueType') {
        const vt = value as string;
        const stillOk = !!f.personField && PERSON_FIELDS.some((p) => p.value === f.personField && p.vts.includes(vt));
        return { ...f, valueType: vt, personField: stillOk ? f.personField : null };
      }
      // A2e #91 — a guest's photo is a square photo: choosing the logo shape drops that link.
      if (name === 'imageShape') {
        return { ...f, imageShape: String(value), personField: value === 'logo' && f.personField === 'photo' ? null : f.personField };
      }
      return { ...f, [name]: value };
    });
  }, []);

  const isTyped = form.questionType === SINGLE_VALUE;
  const isAddress = form.questionType === ADDRESS;
  const isImage = form.questionType === IMAGE;
  const isChoice = isChoiceType(form.questionType);
  // A2e #91 — this form's picture links; a guest's photo only for the photo shape.
  const imageLinkOptions = (IMAGE_LINKS[askedOn] ?? []).filter((o) => o.value !== 'photo' || form.imageShape === 'avatar');
  const showPlaceholder = isTyped && TEXT_VALUE_TYPES.has(form.valueType);

  // Person fields compatible with the current value type (phone → phone, …); an address question links whole or not at all.
  const personFieldOptions = useMemo(
    () =>
      isAddress
        ? [ADDRESS_PERSON_FIELD]
        : PERSON_FIELDS.filter((p) => p.vts.includes(form.valueType)).map((p) => ({ value: p.value, label: p.label })),
    [form.valueType, isAddress],
  );

  // Editing an option keeps a single trailing empty row — as soon as the last
  // one gets a value, a fresh empty row appears (no explicit "add" button).
  const setOption = (i: number, patch: Partial<OptionRow>) =>
    setForm((f) => ({ ...f, options: withTrailingEmpty(f.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o))) }));
  const removeOption = (i: number) =>
    setForm((f) => ({ ...f, options: withTrailingEmpty(f.options.filter((_, idx) => idx !== i)) }));

  const validChoiceOptions = useMemo(() => form.options.filter((o) => o.label.trim()), [form.options]);
  const canSave = !!form.title.trim() && (!isChoice || validChoiceOptions.length >= 1);

  // Each returns the first refusal (null when all saved) — a refused option is never dropped silently (#126).
  const syncOptions = async (questionId: string, current: RsvpQuestion | null, rows: OptionRow[]): Promise<OpResult[]> => {
    const original = current?.options ?? [];
    const edited = isChoice ? rows.filter((o) => o.label.trim()) : [];
    const editedIds = new Set(edited.filter((o) => o.id).map((o) => o.id));
    const ops: Array<() => Promise<OpResult>> = [];

    edited.forEach((o, i) => {
      const params = { label: o.label.trim(), price: o.price, orderNo: (i + 1) * 1000 };
      ops.push(() => o.id ? RsvpQuestionOptionService.update(o.id, params) : RsvpQuestionOptionService.create({ ...params, rsvpQuestionId: questionId }));
    });
    (original as RsvpQuestionOption[]).forEach((o) => {
      const optionId = o.id;
      if (optionId && !editedIds.has(optionId)) {
        ops.push(() => RsvpQuestionOptionService.remove(optionId));
      }
    });

    return refusalsOf(ops);
  };

  // Sync the visibility conditions: ONE rsvp_status condition (the chosen responses) + N guest_group
  // conditions (one per selected group). Both diffed against the question's existing conditions.
  const syncConditions = async (questionId: string, current: RsvpQuestion | null): Promise<OpResult[]> => {
    const ops: Array<() => Promise<OpResult>> = [];

    // rsvp_status — a single condition holding all chosen statuses.
    const statusOrig = (current?.conditions ?? []).filter((c) => c.conditionKind === RSVP_STATUS_CONDITION);
    const statuses = form.rsvpStatuses;
    if (statuses.length > 0) {
      const params = { conditionKind: RSVP_STATUS_CONDITION, operator: 'in', rsvpStatuses: statuses, orderNo: 1000 };
      const existing = statusOrig[0];
      if (existing?.id) {
        const conditionId = existing.id;
        ops.push(() => RsvpQuestionConditionService.update(conditionId, params));
        statusOrig.slice(1).forEach((c) => {
          const id = c.id;
          if (id) {
            ops.push(() => RsvpQuestionConditionService.remove(id));
          }
        });
      } else {
        ops.push(() => RsvpQuestionConditionService.create({ ...params, rsvpQuestionId: questionId }));
      }
    } else {
      statusOrig.forEach((c) => {
        const id = c.id;
        if (id) {
          ops.push(() => RsvpQuestionConditionService.remove(id));
        }
      });
    }

    // guest_group — one condition per selected group; create the new ones, remove the deselected ones.
    const groupOrig = (current?.conditions ?? []).filter((c) => c.conditionKind === GUEST_GROUP_CONDITION);
    const origByGroup = new Map(groupOrig.filter((c) => c.guestGroupId).map((c) => [String(c.guestGroupId), c]));
    const wanted = new Set(form.guestGroupIds.map(String));
    for (const gid of form.guestGroupIds) {
      if (!origByGroup.has(String(gid))) {
        ops.push(() => 
          RsvpQuestionConditionService.create({
            rsvpQuestionId: questionId,
            conditionKind: GUEST_GROUP_CONDITION,
            operator: 'in',
            guestGroupId: gid,
            orderNo: 2000,
          }),
        );
      }
    }
    for (const c of groupOrig) {
      if (c.id && (!c.guestGroupId || !wanted.has(String(c.guestGroupId)))) {
        const id = c.id;
        ops.push(() => RsvpQuestionConditionService.remove(id));
      }
    }

    return refusalsOf(ops);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);
    setPartErrors([]);

    // What the server holds — re-read first when the last half-failed save could not confirm it.
    let current = base;
    let rows = form.options;
    if (baseUnconfirmed && base?.id) {
      const reread = await RsvpQuestionService.get(base.id);
      const now = reread.didSucceed() ? (reread.getDataFirst() as RsvpQuestion | undefined) : undefined;

      if (!now) {
        // Nothing is written on a copy that may be stale.
        setSaving(false);
        setSubmitError(reread);

        return;
      }

      current = now;
      rows = adoptSavedOptionIds(rows, now.options ?? []);
      setBase(now);
      setBaseUnconfirmed(false);
      setForm((f) => ({ ...f, options: rows }));
    }

    const input: RsvpQuestionInput = {
      title: form.title.trim(),
      questionType: form.questionType,
      valueType: isTyped ? form.valueType : null,
      personField: isTyped || isAddress || isImage ? form.personField : null,
      // A2e #91 — a picture's shape, merged into whatever else the question's settings hold. Untouched for other types.
      ...(isImage ? { settings: { ...((current?.settings as Record<string, unknown> | null | undefined) ?? {}), image: { shape: form.imageShape } } } : {}),
    // `isRequired` is DERIVED from this by the server, so only the scope is sent.
      requiredScope: form.requiredScope,
      askOncePerGroup: form.askOncePerGroup,
      // ⚠ Create-only on the server, so it is only meaningful on a NEW partner question. An empty
      // list means "every kind" — the same thing a null column means.
      ...(isPartnerForm ? { partnerKinds: form.partnerKinds } : {}),
      helpText: form.helpText.trim() || null,
      placeholder: showPlaceholder ? form.placeholder.trim() || null : null,
    };

    const result = current?.id
      ? await RsvpQuestionService.update(current.id, input)
      // ⚠ The FORM is stated, never inferred. This drawer edits the RSVP form's questions; the
      // partner-application form gets its own entry point. The server has no default to fall back
      // on — `asked_on` is NOT NULL with no database default.
      : await RsvpQuestionService.create(askedOn, { ...input, eventId });

    if (!result.didSucceed()) {
      setSaving(false);
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }

    const saved = result.getDataFirst() as RsvpQuestion | undefined;
    const questionId = current?.id ?? saved?.id;
    if (questionId) {
      // Both, always — the choices and the visibility rules are independent, and a refused choice must not leave a new
      // question live without its rule too.
      const refused = [...(await syncOptions(questionId, current, rows)), ...(await syncConditions(questionId, current))];

      if (refused.length > 0) {
        // The QUESTION is saved; part of what hangs off it is not. Stay open on the saved question, as the server now
        // holds it, and say so — closing as if all went well left a choice question with no options (#126).
        const fresh = await RsvpQuestionService.get(questionId);
        const now = fresh.didSucceed() ? (fresh.getDataFirst() as RsvpQuestion | undefined) : undefined;
        if (now) {
          setBase(now);
          setForm((f) => ({ ...f, options: adoptSavedOptionIds(f.options, now.options ?? []) }));
        } else {
          setBase(saved ?? current ?? null);
          setBaseUnconfirmed(true);
        }
        setPartErrors(refused);
        setSaving(false);
        // The question exists now, so the list shows it.
        onSaved(questionId);

        return;
      }
    }

    setSaving(false);
    // No success toast (house rule): its highlighted row on the list is the confirmation.
    onSaved(questionId ?? null);
    onClose();
  };

  useModalShortcuts({ onConfirm: save, canConfirm: canSave && !saving, enabled: opened });

  // Every refusal reaches the screen — named, since none of these fields has a box of its own here.
  const partErrorText = partErrors.length > 0
    ? [
        t('events:questions.partlySaved', {
          defaultValue: 'The question was saved, but some of its choices or visibility rules were not. Check them and press Save again.',
        }),
        formatErrors(Array.from(new Set(partErrors.flatMap((r) => getFormErrors(r, []).genericErrors)))),
      ]
        .filter(Boolean)
        .join(' ')
    : null;

  // `base`, not the prop: a question this drawer created (a save that half-failed) is being edited now.
  const title = base
    ? t('events:questions.editTitle', { defaultValue: 'Edit question' })
    : t('events:questions.addTitle', { defaultValue: 'Add question' });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      loading={saving}
      errors={partErrorText ? [partErrorText, ...genericErrors] : formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={titleRef}
          name="title"
          label={t('events:questions.question', { defaultValue: 'Question' })}
          placeholder={t('events:questions.questionPh', { defaultValue: 'e.g. Any dietary requirements?' })}
          value={form.title}
          onChange={handleChange}
          required
          error={fieldErrors.title}
        />

        <SelectField
          name="questionType"
          label={t('events:questions.answerType', { defaultValue: 'Answer type' })}
          data={QUESTION_TYPES}
          value={form.questionType}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.questionType}
        />

        {isTyped && (
          <SelectField
            name="valueType"
            label={t('events:questions.format', { defaultValue: 'Format' })}
            data={VALUE_TYPES}
            value={form.valueType}
            onChange={handleChange}
            allowDeselect={false}
            searchable
            error={fieldErrors.valueType}
          />
        )}

        {/* Optionally write the answer back to the guest's profile — only the
            person fields whose type matches the chosen format are offered. */}
        {(isTyped || isAddress) && personFieldOptions.length > 0 && (
          <SelectField
            name="personField"
            label={t('events:questions.personField', { defaultValue: 'Save answer to guest profile' })}
            description={t('events:questions.personFieldHint', { defaultValue: 'Also updates the guest’s saved details' })}
            data={personFieldOptions}
            value={form.personField}
            onChange={handleChange}
            placeholder={t('events:questions.personFieldNone', { defaultValue: 'Don’t save to profile' })}
            clearable
            error={fieldErrors.personField}
          />
        )}

        {isImage && (
          <SelectField
            name="imageShape"
            label={t('events:questions.imageShape', { defaultValue: 'Picture' })}
            description={t('events:questions.imageShapeHint', { defaultValue: 'The website crops the picture to this shape before it is sent.' })}
            data={IMAGE_SHAPES}
            value={form.imageShape}
            onChange={handleChange}
            allowDeselect={false}
          />
        )}

        {isImage && imageLinkOptions.length > 0 && (
          <SelectField
            name="personField"
            label={t('events:questions.imageLink', { defaultValue: 'Also use the picture as' })}
            data={imageLinkOptions}
            value={form.personField}
            onChange={handleChange}
            placeholder={t('events:questions.imageLinkNone', { defaultValue: 'Keep it with the answer only' })}
            clearable
            error={fieldErrors.personField}
          />
        )}

        {isChoice && (
          <div>
            <Text fw={600} size="md" mb="xs">
              {t('events:questions.options', { defaultValue: 'Options' })}
            </Text>
            {/* Column labels live in a header row so every data row is the same
                height and the trash icon lines up (align="center"). */}
            <Group gap="xs" wrap="nowrap" mb={4} px={2}>
              <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                {t('events:questions.optionLabel', { defaultValue: 'Choice' })}
              </Text>
              <Text size="sm" c="dimmed" w={110}>
                {t('events:questions.extraCost', { defaultValue: 'Extra cost' })}
              </Text>
              <Box w={34} />
            </Group>
            {/* ⚠ `options` is a KNOWN form field with no single control — a repeatable editor.
                `getFormErrors` never routes a KNOWN name to the generic alert, so without this
                wrapper the server's refusal on the option list would vanish entirely. */}
            <Input.Wrapper error={fieldErrors.options}>
              <Stack gap="xs">
                {form.options.map((o, i) => {
                  const isTrailingEmpty = i === form.options.length - 1 && !o.label.trim();
                  return (
                    <Group key={o.id ?? `new-${i}`} gap="xs" wrap="nowrap" align="center">
                      <TextField
                        name={`opt-${i}`}
                        placeholder={t('events:questions.optionPh', { defaultValue: 'e.g. Vegetarian' })}
                        value={o.label}
                        onChange={(_n, v) => setOption(i, { label: (v as string) ?? '' })}
                        style={{ flex: 1 }}
                      />
                      <NumberField
                        name={`price-${i}`}
                        placeholder={t('events:questions.free', { defaultValue: 'Free' })}
                        value={o.price}
                        onChange={(_n, v) => setOption(i, { price: (v as number) ?? null })}
                        min={0}
                        w={110}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => removeOption(i)}
                        aria-label={t('common:buttons.remove', { defaultValue: 'Remove' })}
                        style={{ visibility: isTrailingEmpty ? 'hidden' : undefined }}
                      >
                        <AppIcon iconName="Trash" size={16} />
                      </ActionIcon>
                    </Group>
                  );
                })}
              </Stack>
            </Input.Wrapper>
            <Divider my="md" />
          </div>
        )}

        <TextAreaField
          name="helpText"
          label={t('events:questions.helpText', { defaultValue: 'Help text' })}
          description={t('events:questions.helpTextHint', { defaultValue: 'Optional guidance shown under the question' })}
          value={form.helpText}
          onChange={handleChange}
          autosize
          minRows={2}
          maxRows={4}
          error={fieldErrors.helpText}
        />

        {showPlaceholder && (
          <TextField
            name="placeholder"
            label={t('events:questions.placeholder', { defaultValue: 'Placeholder' })}
            description={t('events:questions.placeholderHint', { defaultValue: 'Faint example text inside the field' })}
            value={form.placeholder}
            onChange={handleChange}
            error={fieldErrors.placeholder}
          />
        )}

        <SelectField
          name="requiredScope"
          label={t('events:questions.requiredScope', { defaultValue: 'Who must answer' })}
          description={
            partyOnly
              ? t('events:questions.requiredScopePartyHint', {
                  defaultValue:
                    'This event counts guests by party (headcount): only the main guest is named, so they answer each question once for everyone coming. To ask every guest, switch the event to “Every guest by name” in Event details.',
                })
              : t('events:questions.requiredScopeHint', {
                  defaultValue:
                    "“Adults only” is the right choice for contact details: a child on the booking usually has no email of their own, and making it required means whoever is booking types theirs in instead — which merges the child into their contact.",
                })
          }
          data={
            partyOnly
              ? [...PARTY_SCOPES, ...REQUIRED_SCOPES.filter((r) => r.value === form.requiredScope && !PARTY_SCOPES.some((p) => p.value === r.value))]
              : REQUIRED_SCOPES
          }
          value={form.requiredScope}
          onChange={handleChange}
          allowDeselect={false}
          // A headcount event hides the per-group switch: its refusal, should one come, is said here instead.
          error={fieldErrors.requiredScope || fieldErrors.isRequired || (partyOnly ? fieldErrors.askOncePerGroup : undefined)}
        />

        {/* ⚠ RSVP form only. A sponsorship has ONE applicant, so "ask once per the party" has no
            meaning there — and the server captures partner answers in a mode where the setting is
            deliberately ignored. Showing it would offer a switch that does nothing. */}
        {/* …and on a headcount event every question is already once per party. */}
        {!isApplicationForm && !partyOnly && (
          <SwitchField
            name="askOncePerGroup"
            label={t('events:questions.perGroup', { defaultValue: 'Ask once per group (first guest answers for the party)' })}
            description={t('events:questions.perGroupHint', {
              defaultValue: 'Off = every guest answers it individually.',
            })}
            checked={form.askOncePerGroup}
            onChange={handleChange}
            error={fieldErrors.askOncePerGroup}
          />
        )}

        {/* Which partner kinds get asked this. ⚠ Empty = EVERY kind, said out loud — a host who
            reads an empty picker as "nobody" would scope every question by hand. */}
        {isPartnerForm && (
          <MultiSelectField
            label={t('partners:questions.kinds', { defaultValue: 'Ask this of' })}
            description={t('partners:questions.kindsHint', {
              defaultValue: 'Leave empty to ask every partner kind. Otherwise only the kinds you choose are asked.',
            })}
            placeholder={t('partners:questions.kindsPlaceholder', { defaultValue: 'Every partner kind' })}
            data={PARTNER_KIND_VALUES.map((kind) => ({ value: kind, label: partnerKindLabel(kind) }))}
            value={form.partnerKinds}
            onChange={(_name, next) => setForm((prev) => ({ ...prev, partnerKinds: next }))}
            disabled={!!base?.id}
            error={fieldErrors.partnerKinds}
            size="md"
            searchable
            clearable
          />
        )}

        <Divider />

        {/* Visibility — only show this question for guests with one of the
            chosen responses (e.g. ask "why?" only when someone can't attend).
            ⚠ RSVP form only: an applicant has no rsvp_status and belongs to no guest group, so
            these conditions can never match and the question would simply never appear. */}
        {!isApplicationForm && (
        <>
        <RsvpStatusMultiSelect
          label={t('events:questions.showWhen', { defaultValue: 'Only show for these responses' })}
          description={t('events:questions.showWhenHint', {
            defaultValue: 'Leave empty to always show it. Otherwise it appears only for the selected responses.',
          })}
          value={form.rsvpStatuses}
          onChange={(v) => setForm((f) => ({ ...f, rsvpStatuses: v }))}
          placeholder={form.rsvpStatuses.length ? undefined : t('events:questions.showWhenPh', { defaultValue: 'Always shown' })}
          error={fieldErrors.rsvpStatuses}
        />

        {/* Visibility by guest group — show only for guests in one of the chosen groups. Per-guest
            questions check each guest's groups; a once-per-group question checks the party's groups. */}
        {groupOptions.length > 0 && (
          <MultiSelectField
            label={t('events:questions.showForGroups', { defaultValue: 'Only show for guests in these groups' })}
            description={t('events:questions.showForGroupsHint', {
              defaultValue:
                'Leave empty for any group. Otherwise it appears only for guests in one of the selected groups (combined with the response filter above).',
            })}
            data={groupOptions}
            value={form.guestGroupIds}
            onChange={(_name, v) => setForm((f) => ({ ...f, guestGroupIds: v }))}
            placeholder={form.guestGroupIds.length ? undefined : t('events:questions.showForGroupsPh', { defaultValue: 'Any group' })}
            clearable
            searchable
            size="md"
            comboboxProps={{ withinPortal: true }}
          />
        )}
        </>
        )}
      </Stack>
    </AppDrawer>
  );
};
